// src/components/locations/regions/RegionsTable.tsx
import React from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";

import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import Input from "@/components/shared/ui/Input";
import type { LocationRegion } from "@/api/features/locations/types";

export type RegionsTableFilters = {
  name?: string;
  code?: string;
};

type RegionsTableProps = {
  rows?: LocationRegion[];
  page: number;
  pageSize: number;
  total: number;
  isLoading?: boolean;
  filters: RegionsTableFilters;
  setFilters: React.Dispatch<React.SetStateAction<RegionsTableFilters>>;
  onPageChange: (page: number) => void;
  onEdit: (row: LocationRegion) => void;
  onDelete: (row: LocationRegion) => void;
  countryIso2: string;
};

const RegionsTable: React.FC<RegionsTableProps> = ({
  rows,
  page,
  pageSize,
  total,
  isLoading = false,
  filters,
  setFilters,
  onPageChange,
  onEdit,
  onDelete,
  countryIso2,
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const safePageSize = pageSize > 0 ? pageSize : 10;
  const safeTotal = total || 0;

  const totalPages = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const startIndex = safeTotal === 0 ? 0 : (page - 1) * safePageSize;
  const endIndex =
    safeTotal === 0 ? 0 : Math.min(startIndex + safePageSize, safeTotal);

  const showSkeleton = isLoading && safeRows.length === 0;
  const showEmptyState = !isLoading && safeRows.length === 0;

  const handlePrev = () => {
    if (page > 1) onPageChange(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages) onPageChange(page + 1);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr className="border-b border-gray-200">
              <Th>S.No</Th>
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Name</span>
                  <ColumnFilter label="Name">
                    <Input
                      placeholder="Contains…"
                      value={filters.name || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>
              <Th>
                <div className="flex items-center gap-1.5">
                  <span>Code</span>
                  <ColumnFilter label="Code">
                    <Input
                      placeholder="Exact code…"
                      value={filters.code || ""}
                      onChange={(e) =>
                        setFilters((f) => ({ ...f, code: e.target.value }))
                      }
                    />
                  </ColumnFilter>
                </div>
              </Th>
              <Th>Country</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {showSkeleton && (
              <tr>
                <Td colSpan={5}>
                  <div className="flex items-center justify-center py-10 text-xs text-gray-500">
                    Loading regions…
                  </div>
                </Td>
              </tr>
            )}

            {showEmptyState && (
              <tr>
                <Td colSpan={5}>
                  <div className="flex flex-col items-center justify-center py-10 text-sm text-gray-500">
                    <div>No regions found.</div>
                    <div className="mt-1 text-xs text-gray-400">
                      Try adjusting your search or filters.
                    </div>
                  </div>
                </Td>
              </tr>
            )}

            {!showSkeleton &&
              !showEmptyState &&
              safeRows.map((row, index) => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50/60"
                >
                  <Td className="text-gray-700">
                    {startIndex + index + 1}
                  </Td>

                  <Td className="text-gray-900">
                    {row.name ?? ""}
                  </Td>

                  <Td className="text-gray-700 font-mono text-xs">
                    {row.code ?? "-"}
                  </Td>

                  <Td className="text-gray-700 font-mono text-xs">
                    {(row as any).countryIso2 ?? countryIso2}
                  </Td>

                  <Td>
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                        onClick={() => onEdit(row)}
                        aria-label="Edit region"
                        title="Edit region"
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(row)}
                        aria-label="Delete region"
                        title="Delete region"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-gray-200">
        {showSkeleton && (
          <div className="p-6 text-center text-xs text-gray-500">
            Loading regions…
          </div>
        )}

        {showEmptyState && (
          <div className="p-6 text-center text-sm text-gray-500">
            No regions found.
          </div>
        )}

        {!showSkeleton &&
          !showEmptyState &&
          safeRows.map((row, index) => (
            <div key={row.id} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900">
                    {startIndex + index + 1}. {row.name ?? ""}
                  </div>
                  <div className="mt-0.5 text-xs font-mono text-gray-500 truncate">
                    {row.code ? `Code: ${row.code}` : "Code: -"}
                  </div>
                  <div className="mt-0.5 text-xs font-mono text-gray-500 truncate">
                    {(row as any).countryIso2
                      ? `Country: ${(row as any).countryIso2}`
                      : `Country: ${countryIso2}`}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
                    onClick={() => onEdit(row)}
                    aria-label="Edit region"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    type="button"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 hover:bg-red-50"
                    onClick={() => onDelete(row)}
                    aria-label="Delete region"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-4 py-3">
        <div className="text-xs text-gray-500">
          Showing{" "}
          <span className="font-medium text-gray-700">
            {safeTotal === 0 ? 0 : startIndex + 1}
          </span>{" "}
          to{" "}
          <span className="font-medium text-gray-700">
            {endIndex}
          </span>{" "}
          of{" "}
          <span className="font-medium text-gray-700">
            {safeTotal}
          </span>{" "}
          results
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handlePrev}
            disabled={page <= 1}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FiChevronLeft /> Prev
          </button>

          <div className="hidden sm:flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 6) }).map((_, idx) => {
              const n = idx + 1;
              const active = n === page;
              return (
                <button
                  key={n}
                  type="button"
                  onClick={() => onPageChange(n)}
                  className={`rounded-md px-2.5 py-1.5 text-sm ${
                    active
                      ? "bg-sky-600 text-white"
                      : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              );
            })}
            {totalPages > 6 && (
              <span className="px-1 text-sm text-gray-500">…</span>
            )}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={page >= totalPages}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Next <FiChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
};

type CellProps = {
  children: React.ReactNode;
  className?: string;
  colSpan?: number;
};

function Th({ children, className = "" }: CellProps) {
  return (
    <th
      className={`px-5 py-3.5 text-xs font-semibold uppercase tracking-wide ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "", colSpan }: CellProps) {
  return (
    <td className={`px-5 py-3.5 ${className}`} colSpan={colSpan}>
      {children}
    </td>
  );
}

export default RegionsTable;
